/**
 * Session Management Type Definitions
 * Implements dual timeout system for admin sessions
 */

// Activity tracking types
export interface ActivityState {
  /** Timestamp of last user activity */
  lastActivity: number;
  /** Whether activity detection is enabled */
  isActive: boolean;
}

export interface ActivityEvent {
  /** Type of activity event (mousedown, keydown, etc.) */
  type: string;
  /** Timestamp when event occurred */
  timestamp: number;
}

// Session configuration
export interface SessionConfig {
  /** Inactivity timeout in minutes - auto-logout after no activity */
  idleTimeoutMinutes: number;
  /** Warning duration in minutes before auto-logout */
  warningDurationMinutes: number;
  /** Maximum session duration in minutes (absolute timeout) */
  maxSessionDurationMinutes: number;
  /** Token refresh interval in minutes */
  tokenRefreshIntervalMinutes: number;
  /** DOM events that count as user activity */
  activityEvents: string[];
  /** Activity throttle in milliseconds */
  activityThrottleMs: number;
  /** Enable cross-tab session synchronization */
  enableCrossTabSync: boolean;
  /** Enable automatic form data persistence */
  enableFormAutoSave: boolean;
  /** Form auto-save interval in milliseconds */
  formAutoSaveIntervalMs: number;
}

// Cross-tab sync types
export interface CrossTabMessage {
  type: 'activity' | 'logout' | 'extended' | 'warning';
  timestamp: number;
  data?: any;
}

export interface CrossTabSyncOptions {
  /** Whether cross-tab sync is enabled */
  enabled: boolean;
  /** Callback when logout occurs in another tab */
  onLogoutInOtherTab: () => void;
  /** Callback when activity occurs in another tab */
  onActivityInOtherTab: (timestamp: number) => void;
  /** Callback when session extended in another tab */
  onExtendedInOtherTab: () => void;
}

// Form persistence types
export interface FormPersistenceOptions<T> {
  /** Unique key for storing form data */
  formKey: string;
  /** Initial form data */
  initialData: T;
  /** Whether to enable auto-save */
  enabled: boolean;
  /** Auto-save interval in milliseconds */
  saveIntervalMs: number;
}

export interface PersistedFormData<T> {
  /** Form data */
  data: T;
  /** When data was last saved */
  savedAt: number;
}

// Warning modal types
export interface SessionWarningState {
  /** Whether warning modal is visible */
  isVisible: boolean;
  /** Seconds remaining until logout */
  secondsRemaining: number;
  /** Countdown interval ID */
  countdownIntervalId: number | null;
  /** Auto-logout timer ID */
  autoLogoutTimerId: number | null;
}

// Default session configuration - DUAL TIMEOUT SYSTEM
// Industry standard: Short inactivity timeout + Long absolute timeout
export const DEFAULT_SESSION_CONFIG: SessionConfig = {
  idleTimeoutMinutes: 480, // 8 hours - Auto-logout after a full workday of inactivity
  warningDurationMinutes: 5, // 5 min warning before inactivity logout
  maxSessionDurationMinutes: 10080, // 7 days - Active users stay logged in for a week
  tokenRefreshIntervalMinutes: 60, // Refresh every hour
  activityEvents: ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'],
  activityThrottleMs: 1000,
  enableCrossTabSync: true,
  enableFormAutoSave: true,
  formAutoSaveIntervalMs: 30000, // 30 seconds
};

// Role-based session configurations
export const ROLE_SESSION_CONFIGS: Record<string, Partial<SessionConfig>> = {
  'super-admin': {
    idleTimeoutMinutes: 30, // 30 min inactivity
    maxSessionDurationMinutes: 10080, // 7 days absolute
  },
  'admin': {
    idleTimeoutMinutes: 30, // 30 min inactivity
    maxSessionDurationMinutes: 10080, // 7 days absolute
  },
  'editor': {
    idleTimeoutMinutes: 30, // 30 min inactivity
    maxSessionDurationMinutes: 10080, // 7 days absolute
  },
};

// Session timeout reasons
export type SessionTimeoutReason = 'inactivity' | 'absolute' | 'manual' | 'cross-tab';

export interface SessionTimeoutEvent {
  reason: SessionTimeoutReason;
  timestamp: number;
  lastActivityTimestamp?: number;
  sessionStartTimestamp?: number;
}
